import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";

/* 1A.1 Simple back-office kit: white fields, #1a1a1a text, 1px lines, square corners, brand color on buttons only. */

export const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const day = (iso) => {
    if (!iso) return "";
    const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
export const when = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export const STATUS_LABEL = {
    pending_payment: "Waiting for payment", paid: "Paid", in_production: "Printing", ready: "Ready",
    completed: "Done", cancelled: "Cancelled",
};

export function Primary({ children, onClick, disabled, testid, type = "button", to, full = false, className = "" }) {
    const cls = `inline-flex min-h-[44px] items-center justify-center gap-2 border border-[var(--accent)] bg-[var(--accent)] px-4 text-[16px] font-semibold text-[var(--on-accent)] disabled:opacity-40 ${full ? "w-full" : ""} ${className}`;
    if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
    return <button type={type} onClick={onClick} disabled={disabled} data-testid={testid} className={cls}>{children}</button>;
}

export function Secondary({ children, onClick, disabled, testid, type = "button", to, href, full = false, danger = false, className = "" }) {
    const cls = `inline-flex min-h-[44px] items-center justify-center gap-2 border border-[#1a1a1a] bg-white px-3 text-[16px] font-medium disabled:opacity-40 ${danger ? "text-[#b3261e] border-[#b3261e]" : "text-[#1a1a1a]"} ${full ? "w-full" : ""} ${className}`;
    if (href) return <a href={href} data-testid={testid} className={cls}>{children}</a>;
    if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
    return <button type={type} onClick={onClick} disabled={disabled} data-testid={testid} className={cls}>{children}</button>;
}

export const inputCls = "w-full min-h-[44px] border border-[#1a1a1a] bg-white px-3 text-[16px] text-[#1a1a1a] outline-none placeholder:text-[#767676]";

export function Field({ label, hint, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="block text-[14px] font-semibold text-[#1a1a1a]">{label}</span>
            {hint && <span className="block text-[13px] text-[#4d4d4d]">{hint}</span>}
            <span className="mt-1 block">{children}</span>
        </label>
    );
}

export function Pills({ items, active, onPick, testid }) {
    return (
        <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" data-testid={testid}>
            {items.map((it) => (
                <button key={it.key} role="tab" aria-selected={active === it.key} onClick={() => onPick(it.key)}
                    data-testid={`${testid}-${it.key}`}
                    className={`inline-flex min-h-[44px] shrink-0 items-center gap-1.5 border px-3 text-[15px] font-medium ${active === it.key
                        ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : "border-[#1a1a1a] bg-white text-[#1a1a1a]"}`}>
                    {it.label}{it.count != null && <span className="tabular-nums opacity-80">{it.count}</span>}
                </button>
            ))}
        </div>
    );
}

export function StatusPill({ status, label, testid }) {
    const tone = status === "ready" ? "border-[#1e6b34] text-[#1e6b34]" : status === "cancelled" ? "border-[#767676] text-[#767676]"
        : status === "pending_payment" ? "border-[#b3261e] text-[#b3261e]" : "border-[#1a1a1a] text-[#1a1a1a]";
    return <span data-testid={testid} className={`inline-flex border bg-white px-2 py-0.5 text-[12px] font-semibold uppercase ${tone}`}>{label || STATUS_LABEL[status] || status}</span>;
}

export function Tag({ children, tone = "ink", testid }) {
    const cls = { ink: "border-[#1a1a1a] text-[#1a1a1a]", red: "border-[#b3261e] text-[#b3261e]", green: "border-[#1e6b34] text-[#1e6b34]", grey: "border-[#767676] text-[#4d4d4d]" }[tone];
    return <span data-testid={testid} className={`inline-flex border bg-white px-1.5 py-0.5 text-[12px] font-semibold ${cls}`}>{children}</span>;
}

/** A small panel over the page (8.0): one title, the body, Cancel always available. */
export function Sheet({ title, onClose, children, testid }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-[#1a1a1a]/40 sm:items-center" data-testid={testid} role="dialog" aria-modal="true">
            <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-[#1a1a1a] bg-white p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                    <h2 className="text-[18px] font-semibold text-[#1a1a1a]">{title}</h2>
                    <button onClick={onClose} aria-label="Cancel" data-testid={`${testid}-cancel-x`} className="flex h-11 w-11 items-center justify-center border border-[#1a1a1a] bg-white"><X size={18} /></button>
                </div>
                {children}
                <div className="mt-3">
                    <Secondary onClick={onClose} testid={`${testid}-cancel`} full>Cancel</Secondary>
                </div>
            </div>
        </div>
    );
}

export function Back({ to, label = "Back", testid = "bo-back" }) {
    const navigate = useNavigate();
    return (
        <button onClick={() => (to ? navigate(to) : navigate(-1))} data-testid={testid}
            className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-[#1a1a1a] underline">
            <ArrowLeft size={16} /> {label}
        </button>
    );
}

export function Empty({ title, children, testid }) {
    return (
        <div className="border border-[#1a1a1a] bg-white p-4" data-testid={testid}>
            <p className="text-[16px] font-semibold text-[#1a1a1a]">{title}</p>
            {children}
        </div>
    );
}

export function Inline({ error, onRetry, testid = "bo-error" }) {
    if (!error) return null;
    return (
        <div className="flex flex-wrap items-center gap-3 border border-[#b3261e] bg-white p-3 text-[15px] text-[#b3261e]" data-testid={testid}>
            <span>{error}</span>
            {onRetry && <button onClick={onRetry} className="min-h-[44px] underline" data-testid={`${testid}-retry`}>Try again</button>}
        </div>
    );
}

/** Read and rewrite the query string without losing the other keys (Back keeps filters, 8.0). */
export function useQuery() {
    const loc = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(loc.search);
    const set = (patch, replace = false) => {
        const next = new URLSearchParams(loc.search);
        Object.entries(patch).forEach(([k, v]) => (v == null || v === "" ? next.delete(k) : next.set(k, v)));
        navigate({ pathname: loc.pathname, search: next.toString() ? `?${next}` : "" }, { replace });
    };
    return { params, set, pathname: loc.pathname };
}

export const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
};
